import React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar"
import { LayoutDashboard, CheckSquare, AlarmClockCheck, ListChecks, GitBranch, Link2, Users, Settings, LogOut, Building2, Mail, MessageSquare, ChevronRight, Tag } from "lucide-react"
import { useState, useEffect, useRef } from "react"
import { LogoutConfirmModal } from "@/components/modals/logout-confirm-modal"
import { useUsers } from "@/lib/contexts/user-context"

export function AppSidebar() {
  const [pathname, setPathname] = useState(window.location.pathname)
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)
  const { setOpenMobile, isMobile } = useSidebar()
  const [pendingPath, setPendingPath] = useState(null)
  const navigationTimeoutRef = useRef(null)
  const { currentUser } = useUsers()
  const [isMessagingOpen, setIsMessagingOpen] = useState(pathname.includes('/email-templates') || pathname.includes('/text-templates'))
  const [isTasksOpen, setIsTasksOpen] = useState(pathname.includes('/tasks'))
  
  // Get user permissions from the user context instead of page props
  const userPermissions = currentUser?.permissions || []

  // Helper function to check if user has a specific permission
  const hasPermission = (permission) => userPermissions.includes(permission)

  // Update pathname when navigation occurs
  useEffect(() => {
    const handleNavigation = () => {
      setPathname(window.location.pathname)
      // Keep messaging dropdown open when navigating between messaging pages
      if (window.location.pathname.includes('/email-templates') || window.location.pathname.includes('/text-templates')) {
        setIsMessagingOpen(true)
      }
      // Keep tasks dropdown open when navigating between tasks pages
      if (window.location.pathname.includes('/tasks')) {
        setIsTasksOpen(true)
      }
    }

    router.on('navigate', handleNavigation)
    // Note: Inertia router doesn't support router.off, so we don't need cleanup
  }, [])

  // Monitor pathname changes to detect when navigation completes
  useEffect(() => {
    if (pendingPath && pendingPath === pathname) {
      // Navigation completed, now close the sidebar
      const closeTimeout = setTimeout(() => {
        if (isMobile) {
          setOpenMobile(false)
        }
        setPendingPath(null)
      }, 100) // Small delay for better UX

      return () => {
        if (closeTimeout) clearTimeout(closeTimeout)
      }
    }

    // Clear any pending navigation timeout when pathname changes
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current)
      navigationTimeoutRef.current = null
    }
  }, [pathname, pendingPath, isMobile, setOpenMobile])

  const isActive = (path) => {
    return pathname === path
  }

  const isMessagingActive = () => {
    return pathname.includes('/email-templates') || pathname.includes('/text-templates')
  }

  const handleNavigation = (path) => (e) => {
    // Set the pending path to track when navigation completes
    setPendingPath(path)

    // Set a fallback timeout in case the navigation takes too long
    navigationTimeoutRef.current = setTimeout(() => {
      if (isMobile) {
        setOpenMobile(false)
      }
      setPendingPath(null)
    }, 1000) // Fallback timeout of 1 second
  }

  // Check if user has permission to view tasks
  const canViewTasks = hasPermission('view_own_tasks') || 
                      hasPermission('view_department_tasks') || 
                      hasPermission('view_all_tasks')

  // Check if user has permission to view workflows
  const canViewWorkflows = hasPermission('view_workflows')

  // Check if user has permission to view integrations
  const canViewIntegrations = hasPermission('view_integrations')

  // Check if user has permission to view users
  const canViewUsers = hasPermission('view_department_users') || 
                      currentUser?.roles?.includes('admin')

  // Check if user has permission to view departments
  const canViewDepartments = hasPermission('view_departments')

  // Check if user has permission to view categories (admin only)
  const canViewCategories = currentUser?.roles?.includes('admin')

  // Check if user has permission to view templates
  const canViewEmailTemplates = hasPermission('view_email_templates')
  const canViewTextTemplates = hasPermission('view_text_templates')

  // Check if user has permission to view reports
  const canViewReports = hasPermission('view_reports')

  // Check if user has permission to view settings
  const canViewSettings = hasPermission('view_account_settings')

  return (
    <Sidebar>
      <SidebarHeader className="flex h-16 items-center px-4">
        <div className="flex items-center gap-2 font-semibold">
          <img 
            src="/images/icons/dme-favicon.png" 
            alt="DME Logo" 
            className="h-6 w-6"
          />
          <span className="text-lg">DME</span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/")}>
              <Link href="/" onClick={handleNavigation("/")}>
                <LayoutDashboard className="h-5 w-5" />
                <span>Dashboard</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          
          {canViewTasks && (
            <SidebarMenuItem>
              <SidebarMenuButton 
                onClick={() => setIsTasksOpen(!isTasksOpen)}
                isActive={isActive("/tasks") || isActive("/tasks/today")}
                className="justify-between"
              >
                <div className="flex items-center gap-2">
                  <CheckSquare className="h-5 w-5" />
                  <span>Tasks</span>
                </div>
                <ChevronRight className={`h-4 w-4 transition-transform ${isTasksOpen ? 'rotate-90' : ''}`} />
              </SidebarMenuButton>
              {isTasksOpen && (
                <SidebarMenuSub>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton asChild isActive={isActive("/tasks") && !isActive("/tasks/today")}>
                      <Link href="/tasks" onClick={handleNavigation("/tasks")}>
                        <ListChecks className="h-4 w-4" />
                        <span>All Tasks</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton asChild isActive={isActive("/tasks/today")}>
                      <Link href="/tasks/today" onClick={handleNavigation("/tasks/today")}>
                        <AlarmClockCheck className="h-4 w-4" />
                        <span>Today's Tasks</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              )}
            </SidebarMenuItem>
          )}
          
          {canViewWorkflows && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={isActive("/workflows")}>
                <Link href="/workflows" onClick={handleNavigation("/workflows")}>
                  <GitBranch className="h-5 w-5" />
                  <span>Workflows</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          
          {canViewIntegrations && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={isActive("/integrations")}>
                <Link href="/integrations" onClick={handleNavigation("/integrations")}>
                  <Link2 className="h-5 w-5" />
                  <span>Integrations</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          
          {canViewUsers && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={isActive("/users")}>
                <Link href="/users" onClick={handleNavigation("/users")}>
                  <Users className="h-5 w-5" />
                  <span>Users</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          
          {canViewDepartments && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={isActive("/departments")}>
                <Link href="/departments" onClick={handleNavigation("/departments")}>
                  <Building2 className="h-5 w-5" />
                  <span>Departments</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          
          {canViewCategories && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={isActive("/categories")}>
                <Link href="/categories" onClick={handleNavigation("/categories")}>
                  <Tag className="h-5 w-5" />
                  <span>Categories</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          
          {(canViewEmailTemplates || canViewTextTemplates) && (
            <SidebarMenuItem>
              <SidebarMenuButton 
                onClick={() => setIsMessagingOpen(!isMessagingOpen)}
                isActive={isMessagingActive()}
                className="justify-between"
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  <span>Messaging Templates</span>
                </div>
                <ChevronRight className={`h-4 w-4 transition-transform ${isMessagingOpen ? 'rotate-90' : ''}`} />
              </SidebarMenuButton>
              {isMessagingOpen && (
                <SidebarMenuSub>
                  {canViewEmailTemplates && (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton asChild isActive={isActive("/email-templates")}>
                        <Link href="/email-templates" onClick={handleNavigation("/email-templates")}>
                          <Mail className="h-4 w-4" />
                          <span>Email Templates</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  )}
                  {canViewTextTemplates && (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton asChild isActive={isActive("/text-templates")}>
                        <Link href="/text-templates" onClick={handleNavigation("/text-templates")}>
                          <MessageSquare className="h-4 w-4" />
                          <span>Text Templates</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  )}
                </SidebarMenuSub>
              )}
            </SidebarMenuItem>
          )}
        </SidebarMenu>
        <SidebarSeparator />
        <SidebarMenu>
          {canViewSettings && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={isActive("/settings")}>
                <Link href="/settings" onClick={handleNavigation("/settings")}>
                  <Settings className="h-5 w-5" />
                  <span>Settings</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => setIsLogoutModalOpen(true)}>
              <LogOut className="h-5 w-5" />
              <span>Logout</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <LogoutConfirmModal open={isLogoutModalOpen} onClose={() => setIsLogoutModalOpen(false)} />
    </Sidebar>
  )
}
